const FONT_FAMILY = '"Inter", "Helvetica Neue", Arial, sans-serif';
const COLLAB_SYMBOL = 'x';
// Keep the exported watermark compact so it never overwhelms the artwork
const MIN_LOGO_HEIGHT = 22;
const MAX_LOGO_HEIGHT = 56;
const MIN_FONT_SIZE = 18;
const MAX_FONT_SIZE = 20;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function measureUsername(ctx, text, fontSize, maxContentWidth, logoWidth, gap) {
  if (!ctx || !text || maxContentWidth <= 0) {
    return null;
  }

  let currentSize = fontSize;
  let metrics = null;
  let crossMetrics = null;

  for (let attempts = 0; attempts < 8; attempts += 1) {
    ctx.font = `600 ${currentSize}px ${FONT_FAMILY}`;
    ctx.textBaseline = 'alphabetic';
    metrics = ctx.measureText(text);
    crossMetrics = ctx.measureText(COLLAB_SYMBOL);

    const usernameWidth = metrics.width;
    const crossWidth = crossMetrics.width;
    const requiredWidth = usernameWidth + gap + crossWidth + gap + logoWidth;

    if (requiredWidth <= maxContentWidth || currentSize <= MIN_FONT_SIZE) {
      return {
        fontSize: currentSize,
        metrics,
        crossWidth,
      };
    }

    currentSize = clamp(currentSize * 0.9, MIN_FONT_SIZE, currentSize);
  }

  ctx.font = `600 ${MIN_FONT_SIZE}px ${FONT_FAMILY}`;
  metrics = ctx.measureText(text);
  crossMetrics = ctx.measureText(COLLAB_SYMBOL);
  return {
    fontSize: MIN_FONT_SIZE,
    metrics,
    crossWidth: crossMetrics.width,
  };
}

export default function drawUsernameWordmark(ctx, logoImage, width, height, username) {
  if (!ctx || !logoImage || !width || !height) {
    return;
  }

  const vwEquivalent = width * 0.01;
  const targetLogoHeight = clamp(vwEquivalent, MIN_LOGO_HEIGHT, MAX_LOGO_HEIGHT);
  const targetFontSize = clamp(targetLogoHeight * 0.92, MIN_FONT_SIZE, MAX_FONT_SIZE);

  const margin = Math.max(width, height) * 0.03;
  const maxContentWidth = width - margin * 2;
  const rawLogoWidth = logoImage.naturalWidth || logoImage.width || 1;
  const rawLogoHeight = logoImage.naturalHeight || logoImage.height || 1;
  const logoHeight = targetLogoHeight;
  const logoWidth = (rawLogoWidth / rawLogoHeight) * logoHeight;

  const trimmedUsername = typeof username === 'string' ? username.trim() : '';
  const hasUsername = Boolean(trimmedUsername);
  const displayUsername = trimmedUsername.toUpperCase();
  const gap = hasUsername ? targetFontSize * 0.35 : 0;

  const usernameData = hasUsername
    ? measureUsername(ctx, displayUsername, targetFontSize, maxContentWidth, logoWidth, gap)
    : null;

  const usernameWidth = usernameData?.metrics?.width ?? 0;
  const crossWidth = usernameData?.crossWidth ?? 0;
  const textBlockWidth = usernameWidth ? usernameWidth + gap + crossWidth + gap : 0;
  const watermarkWidth = logoWidth + textBlockWidth;
  const baseX = width - margin - watermarkWidth;
  const logoX = baseX + textBlockWidth;
  const logoY = height - margin - logoHeight;

  if (usernameData?.metrics && usernameWidth > 0) {
    const ascent = usernameData.metrics.actualBoundingBoxAscent ?? usernameData.fontSize;
    const descent =
      usernameData.metrics.actualBoundingBoxDescent ?? usernameData.fontSize * 0.2;
    const centerY = logoY + logoHeight / 2;
    const baselineY = centerY + (ascent - descent) / 2;
    const textY = clamp(baselineY, margin, height - margin);

    ctx.save();
    ctx.filter = 'none';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.font = `600 ${usernameData.fontSize}px ${FONT_FAMILY}`;
    ctx.fillText(displayUsername, baseX, textY);
    ctx.fillText(COLLAB_SYMBOL, baseX + usernameWidth + gap, textY);
    ctx.restore();
  }

  ctx.save();
  ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
  ctx.restore();
}
